import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Paper,
} from '@mui/material';
import MarkdownEditor from '@uiw/react-markdown-editor';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useRef, useState } from 'react';
import NewsMedia from './NewsMedia';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { CommentInput, CommentMessage } from './Components/NewsCommentary';
import LikeReaction from './Components/NewsLikeReaction';
import { getAbsoluteFileUrl } from 'src/app/helpers/utils';
import PanoramaViewer from './Components/PanoramaViewer';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { useGetCommentsByArticleQuery } from 'src/app/services/articlesCommentsApi';

const NewsItemModal = ({ openNews, handleCloseNews, post, refetchList, handleReactionArticle }) => {
  const { t } = useTranslation('translations');
  const { isDarkMode } = useSelector((state) => state.themeSlice);
  const [resizeViewport, setResizeViewport] = useState(window.innerWidth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const ref = useRef(null);
  const [page, setPage] = useState(1);

  const [visibleCount, setVisibleCount] = useState(3);

  const {
    data: commentData,
    isLoading: isCommentsLoading,
    refetch: refetchComments,
  } = useGetCommentsByArticleQuery({
    articleId: post.id,
    page,
    limit: 100,
  });
  function scrollToBottom() {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  }
  useEffect(() => {
    window.addEventListener('resize', function () {
      setResizeViewport(window.innerWidth);
    });
  }, [resizeViewport]);

  useEffect(() => {
    const root = document.getElementById('root');

    if (openNews) {
      root?.setAttribute('inert', '');
      document.body.style.overflow = 'hidden';
    } else {
      root?.removeAttribute('inert');
      document.body.style.overflow = '';
    }

    return () => {
      root?.removeAttribute('inert');
      document.body.style.overflow = '';
    };
  }, [openNews]);

  return (
    <Dialog
      sx={{
        width: resizeViewport >= 1300 ? '50%' : resizeViewport >= 750 ? '75%' : '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
      onClose={handleCloseNews}
      open={openNews}
      maxWidth="sm"
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        <div className="line-clamp-1 overflow-auto text-ellipsis whitespace-nowrap max-w-full hover:line-clamp-none">
          {post.title}
        </div>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleCloseNews}
        sx={{
          position: 'absolute',
          right: 2,
          top: 2,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ pb: 3 }}>
        <div className="overflow-auto px-4 pt-4 pb-3  flex-1">
          <div className="rounded-lg h-auto lg:h-[400px] overflow-hidden font-inter font-900 relative">
            <NewsMedia
              media={post.fileList?.length > 0 ? post.fileList : []}
              imageType={post.fileList?.length > 0 ? 1 : 0}
            />
            {post?.isPanorama && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="mt-4 px-4 py-2 bg-blue-400 text-white rounded hover:bg-blue-700 absolute bottom-5 right-5 
             transition-transform duration-200 ease-in-out transform hover:scale-110"
              >
                <FullscreenIcon />
              </button>
            )}
          </div>
          {isModalOpen && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0,0,0,0.9)',
                zIndex: 9999,
              }}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-10 right-10 z-[10000] px-6 py-5 rounded-full text-black bg-gray-300 hover:bg-gray-300/70 transition-colors"
              >
                <CloseIcon />
              </button>

              <PanoramaViewer
                imageUrl={getAbsoluteFileUrl(post?.fileList[0].contentPath)}
                className="w-full h-full"
                type="fullwidth"
              />
            </div>
          )}
          <DialogContentText
            component="div"
            id="alert-dialog-description"
            className="mt-16 font-inter"
          >
            <MarkdownEditor.Markdown
              source={post.text}
              style={{
                backgroundColor: 'transparent',
                color: isDarkMode ? '#cdcdcd' : '#444444',
              }}
            />
          </DialogContentText>
          <div className="mt-14">
            <p className="font-inter text-12 text-darkgrey">
              {moment(post.createdDate).format('DD.MM.YYYY HH:mm')}
            </p>
          </div>
        </div>
        <div ref={ref}></div>
        <LikeReaction
          post={post}
          refetchList={refetchList}
          handleReactionArticle={handleReactionArticle}
        />
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
          {isCommentsLoading ? (
            <div>{t('LoadingComments')}...</div>
          ) : commentData?.items?.length > 0 ? (
            <>
              {commentData.items.slice(0, visibleCount).map((el) => (
                <CommentMessage
                  key={el.id}
                  name={`${el.tbStaff?.fullName?.surname || ''} ${
                    el.tbStaff?.fullName?.name || ''
                  } ${el.tbStaff?.fullName?.patronymic || ''}`}
                  avatarUrl={getAbsoluteFileUrl(el.tbStaff?.staffFileUrl || null)}
                  message={el.text}
                  timeAgo={el.createdDate}
                  id={el.id}
                  refetchList={refetchComments}
                  tbStaffId={el.tbStaffId}
                />
              ))}
              {commentData.items.length > visibleCount && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={() => setVisibleCount((prev) => prev + 10)}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                  >
                    {t('ShowMore')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div>{t('StartDiscussion')}</div>
          )}
        </Paper>
      </DialogContent>
      <CommentInput
        refetchList={refetchComments}
        articleId={post.id}
        scrollToBottom={scrollToBottom}
      />
    </Dialog>
  );
};

export default NewsItemModal;
